import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Globe2 } from "lucide-react";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/settings/region")({
  head: () => ({
    meta: [
      { title: "Language & Region — Quibblies" },
      {
        name: "description",
        content:
          "Choose your interface language, preferred currency, timezone and reading languages on Quibblies.",
      },
      { property: "og:title", content: "Language & Region — Quibblies" },
      { property: "og:description", content: "Your global preferences on Quibblies." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RegionSettings,
});

const COUNTRIES = [
  ["GB", "United Kingdom"],
  ["US", "United States"],
  ["CA", "Canada"],
  ["IE", "Ireland"],
  ["AU", "Australia"],
  ["NZ", "New Zealand"],
  ["FR", "France"],
  ["DE", "Germany"],
  ["ES", "Spain"],
  ["IT", "Italy"],
  ["PT", "Portugal"],
  ["NL", "Netherlands"],
  ["SE", "Sweden"],
  ["NO", "Norway"],
  ["DK", "Denmark"],
  ["PL", "Poland"],
  ["BR", "Brazil"],
  ["MX", "Mexico"],
  ["AR", "Argentina"],
  ["ZA", "South Africa"],
  ["IN", "India"],
  ["JP", "Japan"],
  ["SG", "Singapore"],
] as const;

function timezones() {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supported === "function") return supported("timeZone");
  return ["UTC", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Tokyo"];
}

function RegionSettings() {
  const { t, preferences, setPreferences } = useI18n();
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState(preferences);

  React.useEffect(() => setDraft(preferences), [preferences]);

  const { data: languages = [] } = useQuery({
    queryKey: ["languages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("languages")
        .select("code, name, native_name, interface_ready")
        .eq("archived", false)
        .order("position");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: currencies = [] } = useQuery({
    queryKey: ["currencies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("currencies")
        .select("code, symbol, name")
        .eq("enabled", true)
        .order("position");
      if (error) throw error;
      return data ?? [];
    },
  });

  const zones = React.useMemo(timezones, []);

  function toggleContentLanguage(code: string) {
    setDraft((current) => {
      const has = current.content_languages.includes(code);
      const next = has
        ? current.content_languages.filter((item) => item !== code)
        : [...current.content_languages, code];
      return { ...current, content_languages: next.length ? next : [code] };
    });
  }

  async function save() {
    setSaving(true);
    try {
      await setPreferences(draft);
      toast.success("Preferences saved");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <SectionHeading title={t("settings.title")} description={t("settings.subtitle")} />

      <div className="mt-10 space-y-8">
        <section className="rounded-2xl border border-border/70 bg-surface/50 p-6">
          <div className="flex items-center gap-2 text-primary-glow">
            <Globe2 className="h-4 w-4" aria-hidden="true" />
            <h2 className="text-sm font-semibold uppercase tracking-wide">One global community</h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Everyone reads and writes on the same Quibblies. These settings shape your own
            experience only — they never move you to a separate regional site.
          </p>
        </section>

        <section className="grid gap-6 sm:grid-cols-2">
          <div>
            <Label htmlFor="language">{t("settings.interfaceLanguage")}</Label>
            <Select
              value={draft.preferred_language}
              onValueChange={(value) =>
                setDraft((current) => ({ ...current, preferred_language: value }))
              }
            >
              <SelectTrigger id="language" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages
                  .filter((item) => item.interface_ready)
                  .map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.native_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="currency">{t("settings.currency")}</Label>
            <Select
              value={draft.preferred_currency}
              onValueChange={(value) =>
                setDraft((current) => ({ ...current, preferred_currency: value }))
              }
            >
              <SelectTrigger id="currency" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.symbol} {item.code} — {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="timezone">{t("settings.timezone")}</Label>
            <Select
              value={draft.timezone}
              onValueChange={(value) => setDraft((current) => ({ ...current, timezone: value }))}
            >
              <SelectTrigger id="timezone" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {zones.map((zone) => (
                  <SelectItem key={zone} value={zone}>
                    {zone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="country">{t("settings.country")}</Label>
            <Select
              value={draft.country_code ?? ""}
              onValueChange={(value) =>
                setDraft((current) => ({ ...current, country_code: value }))
              }
            >
              <SelectTrigger id="country" className="mt-2">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {COUNTRIES.map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">{t("settings.countryHelp")}</p>
          </div>
        </section>

        <section className="rounded-2xl border border-border/70 bg-surface/50 p-6">
          <h2 className="font-display text-xl text-foreground">{t("settings.contentLanguages")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Used to shape Discover and your feed. You can always browse everything.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {languages.map((item) => (
              <button
                key={item.code}
                type="button"
                onClick={() => toggleContentLanguage(item.code)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-sm transition-smooth",
                  draft.content_languages.includes(item.code)
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border/70 text-muted-foreground hover:text-foreground",
                )}
              >
                {item.native_name}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="only-mine">{t("settings.onlyMyLanguages")}</Label>
              <Switch
                id="only-mine"
                checked={draft.only_my_languages}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, only_my_languages: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="translatable">{t("settings.includeTranslatable")}</Label>
              <Switch
                id="translatable"
                checked={draft.include_translatable}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, include_translatable: checked }))
                }
              />
            </div>
          </div>
        </section>

        <Button variant="hero" size="pill" onClick={save} disabled={saving}>
          {saving ? t("common.loading") : t("common.save")}
        </Button>
      </div>
    </div>
  );
}
